import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Form, Input, Modal, Popconfirm, Select, Space, Switch, Table, Tag, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { del, get, patch, post } from '../../lib/request'
import { errMsg, fmtTime } from '../../lib/api'
import { useAuthStore, ROLE_LABELS, ROLE_COLORS, AdminRole } from '../../stores/auth'
import { hasPermission, PERMISSIONS, PermissionKey } from '../../utils/permissions'

interface AdminUserItem {
  id: number
  username: string
  role: AdminRole
  onDuty: boolean
  wecomUserId?: string | null
  phone?: string | null
  createdAt: string
}

const ROLE_OPTIONS = (Object.keys(ROLE_LABELS) as AdminRole[]).map((r) => ({
  value: r,
  label: ROLE_LABELS[r],
}))

/** 权限键中文名 — 仅用于本页「权限速览」，不导出（避免成为第 4 处权限真源） */
const PERMISSION_LABELS: Record<PermissionKey, string> = {
  DASHBOARD_VIEW: '工作台',
  LEAD_VIEW: '查看线索',
  LEAD_EDIT: '编辑全部线索',
  LEAD_EDIT_OWN: '编辑本人名下线索',
  LEAD_TRANSFER: '转移线索',
  LEAD_CLAIM: '认领线索',
  REFERRER_VIEW: '查看推荐人',
  REFERRER_EDIT_TAGS: '编辑推荐人标签',
  REVIEW_CENTER: '审核中心',
  REWARD_OPERATE: '奖励操作（查看/核算/导出）',
  REWARD_PAYOUT: '奖励发放 / 收款登记',
  REWARD_APPROVE: '奖励审批',
  REWARD_ADJUST_AMOUNT: '调整奖励金额',
  PAYMENT_MODIFY: '修改收款',
  BLACKLIST_VIEW: '黑名单 / 花名册',
  ROSTER_MANAGE: '花名册管理',
  CONFIG_VIEW: '查看系统配置',
  CONFIG_EDIT: '编辑系统配置',
  POSTER_UPLOAD: '上传海报',
  UNIT_VIEW: '查看楼盘字典',
  UNIT_EDIT: '编辑楼盘字典',
  DUTY_VIEW: '查看值班排班',
  DUTY_EDIT: '编辑值班排班',
  USER_VIEW: '查看账号',
  USER_MANAGE: '管理账号',
  REPORT_VIEW: '报表中心',
  AUDIT_VIEW: '操作日志',
  ASSIGNMENT_VIEW: '认领入口',
  CONTENT_EDIT: '编辑项目/礼遇内容',
  ASSIGNMENT_MANAGE: '分配 / 改派线索',
  REFERRER_EDIT: '推荐人编辑',
  BUSINESS_CONFIG_EDIT: '业务配置编辑',
  // thankyou_v2（批次 8.2）：+2 键（32→34），与 permissions.ts / 契约测试同步
  MERCHANT_MANAGE: '商户档案维护',
  QUALIFICATION_REVIEW: '资质审核',
  // P-PROJ-16FA993B0823（2026-10-22）：+1 键（34→35）
  REFERRER_DELETE: '删除推荐人',
}

/**
 * 角色权限速览（新建账号 / 修改角色两个弹窗内联）。
 * 数据源是前端权限表 utils/permissions.ts，与后端 middleware/permissions.ts 互为镜像；
 * 两表若漂移会出现「速览显示有权限但接口 403」，由 Review Gate 逐键比对兜底。
 */
const RolePermissionPreview: React.FC<{ role?: AdminRole }> = ({ role }) => {
  if (!role) {
    return (
      <div style={{ marginTop: -8, marginBottom: 16, fontSize: 12, color: '#999' }}>
        选择角色后显示该角色的权限速览
      </div>
    )
  }
  const keys = (Object.keys(PERMISSIONS) as PermissionKey[]).filter((k) =>
    (PERMISSIONS[k] as string[]).includes(role),
  )
  return (
    <div
      style={{
        marginTop: -8,
        marginBottom: 16,
        padding: '10px 12px',
        background: '#F5F7FA',
        borderRadius: 8,
      }}
    >
      <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
        「{ROLE_LABELS[role]}」权限速览（共 {keys.length} 项，以后端鉴权为准）
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {keys.map((k) => (
          <Tag key={k} color={ROLE_COLORS[role]} style={{ marginInlineEnd: 0 }}>
            {PERMISSION_LABELS[k]}
          </Tag>
        ))}
      </div>
    </div>
  )
}

/**
 * 账号管理（admin_users RBAC，2026-09-07 超管目标保护收紧）：
 * 列表/新建/修改角色/重置密码/onDuty 切换/删除仅 super_admin + admin 可见。
 * 超管账号只有超管能碰：admin 登录时后端 GET 已滤掉 super_admin 行，本页再对超管行隐藏操作按钮兜底；
 * 新建/修改角色的下拉里对非超管隐藏「超级管理员」选项。
 * 后端兜底：非超管 POST/PATCH/DELETE 打 super_admin 一律 403；禁改自己角色、禁删自己（后端 400 拦截）。
 *
 * account_self_v1（2026-09-23）本人模式：无 USER_VIEW 的角色（含 leasing_manager）走 GET/PATCH /admin/users/me，
 * 表格仅显示本人一行，操作只剩「修改密码」（需当前密码，成功后强制重新登录）与「绑定手机」；
 * 值班标记 / 企微 UserID 只读。SA/A（canViewAll）路径与改动前完全一致。
 */
const AccountTab: React.FC = () => {
  const user = useAuthStore((s) => s.user)
  const canManage = hasPermission(user?.role, 'USER_MANAGE')
  // 无 USER_VIEW → 本人模式（/admin/users/me）
  const canViewAll = hasPermission(user?.role, 'USER_VIEW')
  const selfMode = !canViewAll
  const isSuper = user?.role === 'super_admin'
  // 非超管看不到「超级管理员」选项（新建账号 / 修改角色两个下拉共用）
  const roleOptions = isSuper ? ROLE_OPTIONS : ROLE_OPTIONS.filter((o) => o.value !== 'super_admin')

  const [list, setList] = useState<AdminUserItem[]>([])
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)
  const [roleEditing, setRoleEditing] = useState<AdminUserItem | null>(null)
  const [pwdEditing, setPwdEditing] = useState<AdminUserItem | null>(null)
  const [wecomEditing, setWecomEditing] = useState<AdminUserItem | null>(null)
  const [phoneEditing, setPhoneEditing] = useState<AdminUserItem | null>(null)
  const [saving, setSaving] = useState(false)
  const [createForm] = Form.useForm<{ username: string; password: string; role: AdminRole }>()
  const [roleForm] = Form.useForm<{ role: AdminRole }>()
  const [pwdForm] = Form.useForm<{ currentPassword?: string; password: string }>()
  const [wecomForm] = Form.useForm<{ wecomUserId: string }>()
  const [phoneForm] = Form.useForm<{ phone: string }>()
  // Select 为非受控组件，必须用 useWatch 订阅才能让权限速览随选择实时刷新
  const createRole = Form.useWatch('role', createForm)
  const editRole = Form.useWatch('role', roleForm)

  const fetchList = useCallback(() => {
    setLoading(true)
    const req = canViewAll
      ? get<{ items: AdminUserItem[] }>('/admin/users').then((d) => setList(d?.items ?? []))
      : get<AdminUserItem>('/admin/users/me').then((d) => setList(d ? [d] : []))
    req
      .catch((err) => message.warning(errMsg(err, '账号列表加载失败')))
      .finally(() => setLoading(false))
  }, [canViewAll])

  useEffect(() => { fetchList() }, [fetchList])

  const submitCreate = async () => {
    const v = await createForm.validateFields()
    setSaving(true)
    try {
      await post('/admin/users', v)
      message.success('账号已创建')
      setCreating(false)
      createForm.resetFields()
      fetchList()
    } catch (err: unknown) {
      message.error(errMsg(err, '创建失败'))
    } finally {
      setSaving(false)
    }
  }

  const submitRole = async () => {
    if (!roleEditing) return
    const v = await roleForm.validateFields()
    setSaving(true)
    try {
      await patch(`/admin/users/${roleEditing.id}`, { role: v.role })
      message.success('角色已更新')
      setRoleEditing(null)
      fetchList()
    } catch (err: unknown) {
      message.error(errMsg(err, '角色修改失败'))
    } finally {
      setSaving(false)
    }
  }

  const submitPwd = async () => {
    if (!pwdEditing) return
    const v = await pwdForm.validateFields()
    setSaving(true)
    if (selfMode) {
      try {
        await patch('/admin/users/me', { currentPassword: v.currentPassword, password: v.password })
      } catch (err: unknown) {
        message.error(errMsg(err, '密码修改失败'))
        setSaving(false)
        return
      }
      // 后端已 tokenVersion+1，旧 token 失效：清登录态并跳登录页（与 lib/request.ts 401 拦截器同一跳转方式）
      message.success('密码已修改，请用新密码重新登录')
      useAuthStore.getState().logout()
      setTimeout(() => { window.location.href = '/login' }, 800)
      return
    }
    try {
      await patch(`/admin/users/${pwdEditing.id}`, { password: v.password })
      message.success('密码已重置')
      setPwdEditing(null)
      pwdForm.resetFields()
    } catch (err: unknown) {
      message.error(errMsg(err, '密码重置失败'))
    } finally {
      setSaving(false)
    }
  }

  const toggleDuty = (r: AdminUserItem, checked: boolean) =>
    patch(`/admin/users/${r.id}`, { onDuty: checked })
      .then(() => { message.success(checked ? '已设为值班' : '已取消值班'); fetchList() })
      .catch((err: unknown) => message.error(errMsg(err, '值班标记更新失败')))

  const submitWecom = async () => {
    if (!wecomEditing) return
    const v = await wecomForm.validateFields()
    setSaving(true)
    try {
      // 空串视为置空（后端归一为 null）
      await patch(`/admin/users/${wecomEditing.id}`, { wecomUserId: (v.wecomUserId ?? '').trim() })
      message.success('企业微信 UserID 已保存')
      setWecomEditing(null)
      wecomForm.resetFields()
      fetchList()
    } catch (err: unknown) {
      message.error(errMsg(err, '保存失败'))
    } finally {
      setSaving(false)
    }
  }

  const submitPhone = async () => {
    if (!phoneEditing) return
    const v = await phoneForm.validateFields()
    setSaving(true)
    try {
      // 空串视为解绑（后端归一为 null）
      await patch(selfMode ? '/admin/users/me' : `/admin/users/${phoneEditing.id}`, { phone: (v.phone ?? '').trim() })
      message.success('已更新')
      setPhoneEditing(null)
      phoneForm.resetFields()
      fetchList()
    } catch (err: unknown) {
      message.error(errMsg(err, '保存失败'))
    } finally {
      setSaving(false)
    }
  }

  const columns: ColumnsType<AdminUserItem> = [
    { title: 'ID', dataIndex: 'id', key: 'id', render: (v: number) => `#${v}`, width: 80 },
    { title: '用户名', dataIndex: 'username', key: 'username' },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (v: AdminRole) => <Tag color={ROLE_COLORS[v]}>{ROLE_LABELS[v] ?? v}</Tag>,
    },
    {
      title: '值班标记',
      dataIndex: 'onDuty',
      key: 'onDuty',
      render: (v: boolean, r) =>
        canManage ? (
          <Switch checked={v} checkedChildren="值班" unCheckedChildren="—" onChange={(c) => toggleDuty(r, c)} />
        ) : (
          v ? <Tag color="green">值班</Tag> : '—'
        ),
    },
    {
      title: '企业微信 UserID',
      dataIndex: 'wecomUserId',
      key: 'wecomUserId',
      render: (v: string | null | undefined) => (v ? v : '—'),
    },
    {
      title: '通知手机号',
      dataIndex: 'phone',
      key: 'phone',
      render: (v: string | null | undefined) =>
        v ? v : <span style={{ color: '#999' }}>未绑定</span>,
    },
    { title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', render: fmtTime },
    ...(canManage
      ? [{
          title: '操作',
          key: 'actions',
          // 超管目标保护兜底：admin 登录时对 super_admin 行隐藏全部操作（后端 GET 已滤掉超管行，此为防御层）
          render: (_: unknown, r: AdminUserItem) =>
            (r.role === 'super_admin' && !isSuper) ? null : (
            <Space size={4}>
              <Button
                size="small"
                style={{ minHeight: 32 }}
                disabled={r.id === user?.adminId}
                onClick={() => {
                  setRoleEditing(r)
                  roleForm.setFieldsValue({ role: r.role })
                }}
              >
                修改角色
              </Button>
              <Button size="small" style={{ minHeight: 32 }} onClick={() => setPwdEditing(r)}>
                重置密码
              </Button>
              <Button
                size="small"
                style={{ minHeight: 32 }}
                onClick={() => {
                  setWecomEditing(r)
                  wecomForm.setFieldsValue({ wecomUserId: r.wecomUserId ?? '' })
                }}
              >
                企微 UserID
              </Button>
              <Button
                size="small"
                style={{ minHeight: 32 }}
                onClick={() => {
                  setPhoneEditing(r)
                  phoneForm.setFieldsValue({ phone: r.phone ?? '' })
                }}
              >
                绑定手机
              </Button>
              <Popconfirm
                title={`删除账号「${r.username}」？`}
                okText="删除"
                cancelText="取消"
                onConfirm={() =>
                  del(`/admin/users/${r.id}`)
                    .then(() => { message.success('已删除'); fetchList() })
                    .catch((err: unknown) => message.error(errMsg(err, '删除失败')))
                }
              >
                <Button size="small" danger style={{ minHeight: 32 }} disabled={r.id === user?.adminId}>
                  删除
                </Button>
              </Popconfirm>
            </Space>
          ),
        }]
      : []),
    ...(selfMode
      ? [{
          title: '操作',
          key: 'actions',
          render: (_: unknown, r: AdminUserItem) =>
            r.id !== user?.adminId ? null : (
            <Space size={4}>
              <Button size="small" style={{ minHeight: 32 }} onClick={() => setPwdEditing(r)}>
                修改密码
              </Button>
              <Button
                size="small"
                style={{ minHeight: 32 }}
                onClick={() => {
                  setPhoneEditing(r)
                  phoneForm.setFieldsValue({ phone: r.phone ?? '' })
                }}
              >
                绑定手机
              </Button>
            </Space>
          ),
        }]
      : []),
  ]

  return (
    <>
      {canManage && (
        <Space style={{ marginBottom: 12 }}>
          <Button type="primary" style={{ minHeight: 32 }} onClick={() => setCreating(true)}>
            新建账号
          </Button>
        </Space>
      )}
      {selfMode && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="非管理员账号仅可查看与维护本人账号（密码 / 通知手机号）；其余账号对你不可见"
        />
      )}
      <Table
        rowKey="id"
        columns={columns}
        dataSource={list}
        loading={loading}
        pagination={false}
        size="middle"
      />

      {/* 新建账号 */}
      <Modal
        title="新建账号"
        open={creating}
        onOk={submitCreate}
        onCancel={() => setCreating(false)}
        confirmLoading={saving}
        okText="创建"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" preserve={false}>
          <Form.Item
            name="username"
            label="用户名"
            rules={[
              { required: true, message: '请输入用户名' },
              {
                // username_cjk_v1（2026-09-21）：放开中文与连字符，长度按码点计 2-32，
                // 与后端 users.ts POST 校验保持一致（前端仅体验层提示，权威判定在后端）。
                validator: (_: unknown, v: string) => {
                  if (!v) return Promise.resolve() // required 规则兜底空值
                  const t = v.trim()
                  const ok = /^[一-龥a-zA-Z0-9_-]+$/.test(t) && Array.from(t).length >= 2 && Array.from(t).length <= 32
                  return ok ? Promise.resolve() : Promise.reject(new Error('2-32 位中文/字母/数字/下划线/连字符'))
                },
              },
            ]}
          >
            <Input placeholder="登录用户名" maxLength={32} />
          </Form.Item>
          <Form.Item
            name="password"
            label="初始密码"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 8, message: '密码至少 8 位' },
            ]}
          >
            <Input.Password placeholder="至少 8 位" />
          </Form.Item>
          <Form.Item name="role" label="角色" rules={[{ required: true, message: '请选择角色' }]}>
            <Select options={roleOptions} placeholder="选择角色" />
          </Form.Item>
          <RolePermissionPreview role={createRole} />
        </Form>
      </Modal>

      {/* 修改角色 */}
      <Modal
        title={`修改角色 — ${roleEditing?.username ?? ''}`}
        open={!!roleEditing}
        onOk={submitRole}
        onCancel={() => setRoleEditing(null)}
        confirmLoading={saving}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={roleForm} layout="vertical" preserve={false}>
          <Form.Item name="role" label="角色" rules={[{ required: true, message: '请选择角色' }]}>
            <Select options={roleOptions} />
          </Form.Item>
          <RolePermissionPreview role={editRole} />
        </Form>
      </Modal>

      {/* 重置密码 */}
      <Modal
        title={`${selfMode ? '修改密码' : '重置密码'} — ${pwdEditing?.username ?? ''}`}
        open={!!pwdEditing}
        onOk={submitPwd}
        onCancel={() => setPwdEditing(null)}
        confirmLoading={saving}
        okText={selfMode ? '修改' : '重置'}
        cancelText="取消"
        destroyOnClose
      >
        <Form form={pwdForm} layout="vertical" preserve={false}>
          {selfMode && (
            <Form.Item
              name="currentPassword"
              label="当前密码"
              extra="为保护账号安全，修改自己的密码需先验证当前密码"
              rules={[{ required: true, message: '请输入当前密码' }]}
            >
              <Input.Password placeholder="当前登录密码" autoComplete="current-password" />
            </Form.Item>
          )}
          <Form.Item
            name="password"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 8, message: '密码至少 8 位' },
            ]}
          >
            <Input.Password placeholder="至少 8 位" />
          </Form.Item>
          {selfMode && (
            <Alert type="warning" showIcon message="修改成功后当前登录状态失效，需用新密码重新登录。" />
          )}
        </Form>
      </Modal>

      {/* 企业微信 UserID */}
      <Modal
        title={`企业微信 UserID — ${wecomEditing?.username ?? ''}`}
        open={!!wecomEditing}
        onOk={submitWecom}
        onCancel={() => setWecomEditing(null)}
        confirmLoading={saving}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={wecomForm} layout="vertical" preserve={false}>
          <Form.Item
            name="wecomUserId"
            label="企业微信 UserID"
            extra="该招商主理人在企业微信里的账号（通讯录中可见），用于派单时发企微提醒；留空则不发送"
            rules={[{ pattern: /^[a-zA-Z0-9._-]{1,64}$/, message: '≤64 位字母/数字/._-' }]}
          >
            <Input placeholder="留空则不发送企微提醒" maxLength={64} allowClear />
          </Form.Item>
        </Form>
      </Modal>

      {/* 通知手机号 */}
      <Modal
        title={`通知手机号 — ${phoneEditing?.username ?? ''}`}
        open={!!phoneEditing}
        onOk={submitPhone}
        onCancel={() => setPhoneEditing(null)}
        confirmLoading={saving}
        okText="保存"
        cancelText="取消"
        destroyOnClose
      >
        <Form form={phoneForm} layout="vertical" preserve={false}>
          <Form.Item
            name="phone"
            label="通知手机号"
            extra="用于接收新线索派单短信通知；留空表示解绑"
            rules={[{ pattern: /^1\d{10}$/, message: '请输入 11 位手机号' }]}
          >
            <Input placeholder="11 位手机号，留空解绑" maxLength={11} allowClear />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default AccountTab
